import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { makeBatch, describeCoupon } from "@/services/couponsApi";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

export function downloadCodes(codes, name = "brugo-codes") {
  const csv = "code\n" + codes.join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${name}.csv`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Many single-use codes that all work like one existing coupon (e.g. 500 codes for a flyer or partner).
export function BulkCodes({ open, onClose, coupons, initialTemplate }) {
  const typed = coupons.filter((c) => c.mode !== "auto" && !c.batch_id && !c.archived);
  const [f, setF] = useState({ template: "", count: "100", prefix: "", uses: "1" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { if (open) { setF({ template: initialTemplate || typed[0]?.code || typed[0]?.id || "", count: "100", prefix: (initialTemplate || "").slice(0, 8), uses: "1" }); setDone(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const tpl = typed.find((c) => (c.code || c.id) === f.template);
  const go = async () => {
    setBusy(true);
    try {
      const r = await makeBatch({ template: f.template, count: Number(f.count), prefix: f.prefix || f.template, uses_each: Number(f.uses) || 1 });
      setDone(r); toast.success(`${r.codes.length} codes made`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-codes">
        <DialogHeader><DialogTitle className="font-heading">Make codes in bulk</DialogTitle>
          <DialogDescription>Each code works like the coupon you pick, but can be used only once (or as many times as you set). Good for flyers, partners and events.</DialogDescription></DialogHeader>
        {!done ? (
          <div className="space-y-3">
            <div className="space-y-1"><p className="text-[11px] text-muted-foreground">Copy the rules of</p>
              <Select value={f.template} onValueChange={(v) => setF((s) => ({ ...s, template: v, prefix: v.slice(0, 8) }))}><SelectTrigger data-testid="bulk-template"><SelectValue placeholder="Pick a coupon" /></SelectTrigger>
                <SelectContent>{typed.map((c) => <SelectItem key={c.id} value={c.code || c.id}>{c.code || c.id}</SelectItem>)}</SelectContent></Select>
              {tpl && <p className="text-xs text-muted-foreground">{tpl.label || describeCoupon(tpl)}</p>}
              {!typed.length && <p className="text-xs text-muted-foreground">Create a normal coupon first, then make codes from it.</p>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1"><p className="text-[11px] text-muted-foreground">How many</p><Input type="number" value={f.count} onChange={(e) => setF((s) => ({ ...s, count: e.target.value }))} data-testid="bulk-count" /></div>
              <div className="space-y-1"><p className="text-[11px] text-muted-foreground">Start with</p><Input value={f.prefix} onChange={(e) => setF((s) => ({ ...s, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }))} className="font-mono" placeholder="DIWALI" /></div>
              <div className="space-y-1"><p className="text-[11px] text-muted-foreground">Uses per code</p><Input type="number" value={f.uses} onChange={(e) => setF((s) => ({ ...s, uses: e.target.value }))} /></div>
            </div>
            <p className="text-[11px] text-muted-foreground">Codes look like <span className="font-mono">{(f.prefix || f.template || "CODE").slice(0, 12)}-7K3QHM</span> (no 0/O or 1/I, so they're easy to read out). Up to 2,000 at a time.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="bulk-done">
            <p className="text-sm"><b>{done.codes.length}</b> codes ready · batch <span className="font-mono text-xs">{done.batch_id}</span></p>
            <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-secondary/30 p-2 font-mono text-xs columns-3">{done.codes.map((c) => <div key={c}>{c}</div>)}</div>
            <p className="text-[11px] text-muted-foreground">They're grouped as one row in the Coupons list — pause or download them from there any time.</p>
          </div>
        )}
        <DialogFooter>
          {!done ? (
            <><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={go} disabled={busy || !f.template} data-testid="bulk-make">{busy ? "Making…" : `Make ${Number(f.count) || 0} codes`}</Button></>
          ) : (
            <><Button variant="outline" onClick={() => { navigator.clipboard?.writeText(done.codes.join("\n")); toast.success("Copied"); }}><Icons.Copy className="h-4 w-4 mr-1" />Copy</Button>
              <Button onClick={() => downloadCodes(done.codes, done.batch_id)} data-testid="bulk-download"><Icons.Download className="h-4 w-4 mr-1" />Download CSV</Button></>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
